import React, { useState, useRef } from "react";
import Papa from "papaparse";
import { X, Upload, FileDown, CheckCircle2, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { CSV_MODAL } from "@/constants/testIds";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_HEADERS = [
  "address",
  "city",
  "county",
  "listPrice",
  "daysOnMarket",
  "availableDate",
  "sellerPrefTime",
  "notes",
  "agentName",
  "agentPhone",
  "agentEmail",
];

const TEMPLATE_ROW = [
  "742 Beachfront Dr",
  "Wellington",
  "Palm Beach",
  "500000",
  "14",
  "2026-03-15",
  "12:00 PM - 3:00 PM",
  "Long driveway, bring water",
  "Sarah Jenkins",
  "561-555-0123",
  "sarah@kw.com",
];

const REQUIRED = ["address", "city", "county", "listPrice", "daysOnMarket", "availableDate", "agentName", "agentPhone", "agentEmail"];

const validateRow = (row, idx) => {
  const errors = [];
  for (const field of REQUIRED) {
    if (!String(row[field] ?? "").trim()) errors.push(`${field} is required`);
  }
  const price = Number(String(row.listPrice ?? "").replace(/[^0-9.]/g, ""));
  if (row.listPrice && (Number.isNaN(price) || price <= 0)) errors.push("listPrice must be a positive number");
  const dom = Number(row.daysOnMarket);
  if (row.daysOnMarket && (Number.isNaN(dom) || dom < 0)) errors.push("daysOnMarket must be a non-negative integer");
  if (row.availableDate && !/^\d{4}-\d{2}-\d{2}$/.test(String(row.availableDate).trim())) {
    errors.push("availableDate must be YYYY-MM-DD");
  }
  if (row.agentEmail && !/^\S+@\S+\.\S+$/.test(String(row.agentEmail).trim())) {
    errors.push("agentEmail is invalid");
  }
  return { rowNum: idx + 2, errors, data: row };
};

const rowToPayload = (row) => ({
  address: String(row.address).trim(),
  city: String(row.city).trim(),
  county: String(row.county).trim(),
  listPrice: Number(String(row.listPrice).replace(/[^0-9.]/g, "")),
  daysOnMarket: Number(row.daysOnMarket),
  availableDate: String(row.availableDate).trim(),
  sellerPrefTime: String(row.sellerPrefTime ?? "").trim(),
  notes: String(row.notes ?? "").trim(),
  agentName: String(row.agentName).trim(),
  agentPhone: String(row.agentPhone).trim(),
  agentEmail: String(row.agentEmail).trim(),
});

export const CsvImportModal = ({ open, onOpenChange, onImported }) => {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState([]);
  const [validation, setValidation] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setRows([]);
    setValidation([]);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    Papa.parse(f, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (h) => h.trim(),
      complete: (result) => {
        const parsed = result.data || [];
        setRows(parsed);
        setValidation(parsed.map((r, i) => validateRow(r, i)));
      },
      error: (err) => {
        toast.error("Couldn't parse CSV", { description: err.message });
      },
    });
  };

  const downloadTemplate = () => {
    const csv = Papa.unparse({ fields: TEMPLATE_HEADERS, data: [TEMPLATE_ROW] });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "open-house-opportunities-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const validRows = validation.filter((v) => v.errors.length === 0);
  const invalidCount = validation.length - validRows.length;

  const handleSubmit = async () => {
    if (validRows.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const resp = await fetch(`${API}/listings/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ listings: validRows.map((v) => rowToPayload(v.data)) }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      toast.success(`Imported ${data.count} opportunit${data.count === 1 ? "y" : "ies"}`, {
        description: invalidCount > 0 ? `${invalidCount} row(s) skipped due to validation errors.` : undefined,
      });
      onImported?.(data.listings || []);
      reset();
      onOpenChange(false);
    } catch (err) {
      console.error("bulk import failed:", err?.message || err);
      toast.error("Import failed", {
        description: "The backend rejected the batch. Please double-check your CSV and try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent
        data-testid={CSV_MODAL.root}
        className="max-w-3xl p-0 gap-0 max-h-[92vh] overflow-hidden bg-white border-0 rounded-3xl shadow-2xl [&>button]:hidden"
      >
        <div className="kw-red-bar h-1 w-full" />

        <DialogHeader className="px-8 py-5 border-b border-[color:var(--kw-border)] flex-row items-center justify-between space-y-0">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[color:var(--kw-red)] mb-1">
              Bulk Upload
            </div>
            <DialogTitle className="font-display text-2xl md:text-3xl font-black tracking-tight text-[color:var(--kw-charcoal)] uppercase">
              Import Opportunities from CSV
            </DialogTitle>
          </div>
          <DialogClose
            data-testid={CSV_MODAL.closeBtn}
            className="text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-charcoal)]"
          >
            <X size={22} strokeWidth={2.5} />
          </DialogClose>
        </DialogHeader>

        <div className="overflow-y-auto kw-scroll px-8 py-6 space-y-5" style={{ maxHeight: "calc(92vh - 96px)" }}>
          {/* Instructions */}
          <div className="rounded-2xl bg-[color:var(--kw-cream)] border border-[color:var(--kw-border)] px-5 py-4 text-sm text-[color:var(--kw-charcoal-soft)] leading-relaxed">
            <p>
              Upload a CSV with one row per opportunity. Required columns:{" "}
              <span className="font-bold text-[color:var(--kw-charcoal)]">
                address, city, county, listPrice, daysOnMarket, availableDate (YYYY-MM-DD), agentName, agentPhone, agentEmail
              </span>
              . Optional: <span className="font-bold text-[color:var(--kw-charcoal)]">sellerPrefTime, notes</span>.
            </p>
            <button
              data-testid={CSV_MODAL.downloadTemplateBtn}
              onClick={downloadTemplate}
              className="mt-3 inline-flex items-center gap-2 text-xs font-black tracking-widest uppercase text-[color:var(--kw-red)] hover:text-[color:var(--kw-red-dark)] transition-colors"
            >
              <FileDown size={14} strokeWidth={2.5} />
              Download template
            </button>
          </div>

          {/* File picker */}
          {!file ? (
            <label
              htmlFor="csv-file-picker"
              className="flex flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-[color:var(--kw-border)] hover:border-[color:var(--kw-red)] bg-[color:var(--kw-cream)]/40 hover:bg-[color:var(--kw-cream)] transition-all px-6 py-10 cursor-pointer"
            >
              <div className="w-11 h-11 rounded-full bg-white border border-[color:var(--kw-border)] flex items-center justify-center">
                <Upload size={18} className="text-[color:var(--kw-red)]" strokeWidth={2.5} />
              </div>
              <div className="text-center">
                <div className="font-bold text-[color:var(--kw-charcoal)]">Choose a CSV file</div>
                <div className="text-xs text-[color:var(--muted-foreground)] mt-0.5">or drag &amp; drop here</div>
              </div>
            </label>
          ) : (
            <div className="rounded-2xl border border-[color:var(--kw-border)] bg-white p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[color:var(--kw-teal)]/12 flex items-center justify-center">
                <CheckCircle2 size={18} className="text-[color:var(--kw-teal-dark)]" strokeWidth={2.5} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-bold text-[color:var(--kw-charcoal)] truncate">{file.name}</div>
                <div className="text-xs text-[color:var(--muted-foreground)]">
                  {rows.length} row{rows.length === 1 ? "" : "s"} parsed · {validRows.length} valid · {invalidCount} with errors
                </div>
              </div>
              <button
                type="button"
                onClick={reset}
                className="text-xs font-bold uppercase tracking-widest text-[color:var(--muted-foreground)] hover:text-[color:var(--kw-red)]"
              >
                Change
              </button>
            </div>
          )}
          <input
            id="csv-file-picker"
            ref={inputRef}
            data-testid={CSV_MODAL.fileInput}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => handleFile(e.target.files?.[0])}
            className="hidden"
          />

          {/* Preview */}
          {rows.length > 0 && (
            <div className="rounded-2xl border border-[color:var(--kw-border)] overflow-hidden">
              <div className="max-h-72 overflow-auto kw-scroll">
                <table className="w-full text-xs">
                  <thead className="bg-[color:var(--kw-charcoal)] text-white">
                    <tr>
                      <th className="text-left px-3 py-2">Row</th>
                      <th className="text-left px-3 py-2">Address</th>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Agent</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {validation.map((v, i) => (
                      <tr
                        key={i}
                        data-testid={CSV_MODAL.previewRow(i)}
                        className={i % 2 === 0 ? "bg-white" : "bg-[color:var(--kw-cream)]/40"}
                      >
                        <td className="px-3 py-2 border-t border-[color:var(--kw-border)] text-[color:var(--muted-foreground)]">{v.rowNum}</td>
                        <td className="px-3 py-2 border-t border-[color:var(--kw-border)] text-[color:var(--kw-charcoal)] font-semibold">
                          {v.data.address || "—"}
                        </td>
                        <td className="px-3 py-2 border-t border-[color:var(--kw-border)]">{v.data.availableDate || "—"}</td>
                        <td className="px-3 py-2 border-t border-[color:var(--kw-border)]">{v.data.agentName || "—"}</td>
                        <td className="px-3 py-2 border-t border-[color:var(--kw-border)]">
                          {v.errors.length === 0 ? (
                            <span className="inline-flex items-center gap-1 text-[color:var(--kw-teal-dark)] font-bold">
                              <CheckCircle2 size={11} strokeWidth={2.5} /> Ready
                            </span>
                          ) : (
                            <span
                              className="inline-flex items-center gap-1 text-[color:var(--kw-red-dark)] font-bold"
                              title={v.errors.join("; ")}
                            >
                              <AlertCircle size={11} strokeWidth={2.5} /> {v.errors.length} error{v.errors.length === 1 ? "" : "s"}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {invalidCount > 0 && (
            <div
              data-testid={CSV_MODAL.errorList}
              className="rounded-xl border border-[color:var(--kw-red)]/25 bg-[#fff8f8] px-4 py-3 text-xs text-[color:var(--kw-red-dark)] leading-relaxed"
            >
              <div className="font-black uppercase tracking-widest mb-1">Rows with errors will be skipped</div>
              <ul className="list-disc list-inside space-y-0.5">
                {validation
                  .filter((v) => v.errors.length > 0)
                  .slice(0, 6)
                  .map((v, i) => (
                    <li key={i}>
                      Row {v.rowNum}: {v.errors.join(", ")}
                    </li>
                  ))}
                {validation.filter((v) => v.errors.length > 0).length > 6 && <li>…and more</li>}
              </ul>
            </div>
          )}

          <div className="pt-1">
            <button
              data-testid={CSV_MODAL.submitBtn}
              onClick={handleSubmit}
              disabled={validRows.length === 0 || submitting}
              className={`w-full py-4 rounded-full text-base kw-btn-primary inline-flex items-center justify-center gap-2 ${(validRows.length === 0 || submitting) ? "opacity-40 cursor-not-allowed" : ""}`}
            >
              <Upload size={18} strokeWidth={2.5} />
              {submitting
                ? "Importing…"
                : validRows.length > 0
                ? `Import ${validRows.length} Opportunit${validRows.length === 1 ? "y" : "ies"}`
                : "Import Opportunities"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CsvImportModal;
